import type { WizardState } from './types'
import { SectionBlock, FieldRow, inputStyle } from './ui'

// ─── Icons ───────────────────────────────────────────────────────────────────
const IconCoin = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v2m0 8v2M9.5 9.5A2.5 2.5 0 0 1 12 8h.5a2 2 0 0 1 0 4h-1a2 2 0 0 0 0 4h.5a2.5 2.5 0 0 0 2.5-2.5" />
  </svg>
)
const IconCalendar = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
)
const IconReceipt = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 2h16v20l-4-2-4 2-4-2-4 2V2z" />
    <path d="M9 7h6M9 11h6M9 15h4" />
  </svg>
)
// Summary row icons
const IconHandCoin = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 15h2a2 2 0 0 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17" />
    <path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
    <path d="m2 16 6 6" />
    <circle cx="16" cy="7" r="3" />
  </svg>
)
const IconCoins = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="6" />
    <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
    <path d="M7 6h1v4" /><path d="m16.71 13.88.7.71-2.82 2.82" />
  </svg>
)
const IconWallet = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
  </svg>
)
const IconSummaryHeader = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 2h16v20l-4-2-4 2-4-2-4 2V2z" />
    <path d="M9 7h6M9 11h6M9 15h4" />
  </svg>
)

// ─── Helpers ─────────────────────────────────────────────────────────────────
const pillBtn = (active: boolean): React.CSSProperties => ({
  padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
  border: `1px solid ${active ? '#4f46e5' : '#d1d5db'}`,
  background: active ? '#4f46e5' : '#fff',
  color: active ? 'white' : '#6b7280',
  transition: 'all 0.12s',
})

function parseDkk(s: string): number {
  return parseFloat(s.replace(/\./g, '').replace(',', '.') || '0') || 0
}

function fmtDkk(n: number): string {
  if (!n) return ''
  return n.toLocaleString('da-DK', { maximumFractionDigits: 0 })
}

function fmtDkk2(n: number): string {
  return n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(iso: string): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `d. ${d}-${m}-${y}`
}

// Prorated rent for the move-in month: indflytningsdato (inclusive) → month end.
// round(monthly * (days_in_month - move_in_day + 1) / days_in_month).
// Returns 0 when start_date or monthly rent is missing — mirrors the backend.
function proratedFirstRent(startIso: string, monthly: number): number {
  if (!startIso || !monthly) return 0
  const [y, m, d] = startIso.split('-').map(Number)
  if (!y || !m || !d) return 0
  const daysInMonth = new Date(y, m, 0).getDate()
  return Math.round(monthly * (daysInMonth - d + 1) / daysInMonth)
}

// 1st of the month AFTER the move-in month, as ISO YYYY-MM-DD.
function firstOfNextMonth(startIso: string): string {
  if (!startIso) return ''
  const [y, m] = startIso.split('-').map(Number)
  if (!y || !m) return ''
  const year = m === 12 ? y + 1 : y
  const month = m === 12 ? 1 : m + 1
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`
}

function monthsOfRent(monthly: string, amount: string): number {
  const m = parseDkk(monthly)
  if (!m) return 0
  return parseDkk(amount) / m
}

// ─── Month pill row ───────────────────────────────────────────────────────────
function MonthPills({ monthly, value, onChange }: {
  monthly: string
  value: string
  onChange: (v: string) => void
}) {
  const m = parseDkk(monthly)
  const rentMissing = !m
  function setMonths(n: number) {
    if (n === 0) { onChange('0'); return }
    if (rentMissing) return
    onChange(fmtDkk(m * n))
  }
  const current = parseDkk(value)
  const isNone = current === 0
  const months = m && !isNone ? current / m : 0
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
      <button type="button" style={pillBtn(isNone)} onClick={() => setMonths(0)}>Ingen</button>
      {[1, 2, 3].map(n => (
        <button
          key={n}
          type="button"
          disabled={rentMissing}
          title={rentMissing ? 'Udfyld månedlig husleje først' : undefined}
          style={{
            ...pillBtn(!isNone && Math.abs(months - n) < 0.01),
            opacity: rentMissing ? 0.4 : 1,
            cursor: rentMissing ? 'not-allowed' : 'pointer',
          }}
          onClick={() => setMonths(n)}
        >
          {n} mdr.
        </button>
      ))}
    </div>
  )
}

// ─── Over-limit warning ───────────────────────────────────────────────────────
function OverLimitWarn({ monthly, value, limit, label }: { monthly: string; value: string; limit: number; label: string }) {
  const n = parseDkk(value)
  if (!n || n === 0) return null
  const months = monthsOfRent(monthly, value)
  if (!months || months <= limit) return null
  return (
    <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: '#fff7ed', border: '1px solid #fb923c', fontSize: 12, color: '#9a3412' }}>
      {label} er {months.toFixed(1)} mdr. — max er {limit} mdr. husleje (lejelovens §34)
    </div>
  )
}

// ─── Summary row (grand card) ─────────────────────────────────────────────────
function SummaryCardRow({ icon, label, subtitle, amount, date }: {
  icon: React.ReactNode
  label: string
  subtitle: string
  amount: number
  date: string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#4f46e5' }}>{label}</div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{subtitle}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{amount ? fmtDkk2(amount) + ' kr.' : '—'}</div>
        {date && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Betales {date}</div>}
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export function SectionOkonomi({ state, onChange }: {
  state: WizardState
  onChange: (p: Partial<WizardState>) => void
}) {
  const acontoTotal =
    parseDkk(state.aconto_heating_dkk) +
    parseDkk(state.aconto_water_dkk) +
    parseDkk(state.aconto_electricity_dkk) +
    parseDkk(state.aconto_cooling_dkk) +
    parseDkk(state.aconto_antenna_dkk) +
    parseDkk(state.aconto_internet_dkk) +
    parseDkk(state.beboer_amount_dkk) +
    state.extra_contributions.reduce((s, c) => s + parseDkk(c.dkk), 0)

  const engangsTotal = state.engangsudgifter.reduce((s, e) => s + parseDkk(e.dkk), 0)
  // Leje for første periode: rent for the move-in month, prorated from
  // indflytningsdato (inclusive) to month end — matches PDF §4 / pdf_form.py.
  const foersteLeje = proratedFirstRent(state.start_date, parseDkk(state.monthly_rent_dkk))
  const ovrigt = engangsTotal + foersteLeje + acontoTotal
  // Betaler lejeren i alt = depositum + forudbetalt leje + leje for første periode.
  const betalerIAlt = parseDkk(state.deposit_dkk) + parseDkk(state.prepaid_rent_dkk) + foersteLeje
  const grandTotal = betalerIAlt + engangsTotal + acontoTotal
  // Første gang der herefter betales husleje er den 1. i måneden efter indflytning.
  const naesteHuslejeIso = firstOfNextMonth(state.start_date)

  // Subtitle helpers
  function depositSubtitle() {
    const amt = parseDkk(state.deposit_dkk)
    if (!amt) return 'Ingen depositum'
    const m = parseDkk(state.monthly_rent_dkk)
    if (m) {
      const months = amt / m
      if (Math.abs(Math.round(months) - months) < 0.01)
        return `${Math.round(months)} måneders husleje (${fmtDkk2(amt)} kr.)`
    }
    return `${fmtDkk2(amt)} kr.`
  }
  function prepaidSubtitle() {
    const amt = parseDkk(state.prepaid_rent_dkk)
    if (!amt) return 'Ingen forudbetalt leje'
    const m = parseDkk(state.monthly_rent_dkk)
    if (m) {
      const months = amt / m
      if (Math.abs(Math.round(months) - months) < 0.01)
        return `${Math.round(months)} måneders husleje (${fmtDkk2(amt)} kr.)`
    }
    return `${fmtDkk2(amt)} kr.`
  }

  function updateEngangsnavn(i: number, name: string) {
    onChange({ engangsudgifter: state.engangsudgifter.map((e, idx) => idx === i ? { ...e, name } : e) })
  }
  function updateEngangsbeloeb(i: number, dkk: string) {
    onChange({ engangsudgifter: state.engangsudgifter.map((e, idx) => idx === i ? { ...e, dkk } : e) })
  }
  function addEngangslinje() {
    if (state.engangsudgifter.length >= 3) return
    onChange({ engangsudgifter: [...state.engangsudgifter, { name: '', dkk: '' }] })
  }
  function removeEngangslinje(i: number) {
    onChange({ engangsudgifter: state.engangsudgifter.filter((_, idx) => idx !== i) })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Depositum ────────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconCoin />} title="Depositum" subtitle="Sikkerhedsstillelse ved indflytning">
        <FieldRow label="Depositum" required>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              type="text"
              value={state.deposit_dkk}
              onChange={e => onChange({ deposit_dkk: e.target.value })}
              placeholder="25.500"
            />
            <span style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }}>kr.</span>
          </div>
          <MonthPills monthly={state.monthly_rent_dkk} value={state.deposit_dkk} onChange={v => onChange({ deposit_dkk: v })} />
          <OverLimitWarn monthly={state.monthly_rent_dkk} value={state.deposit_dkk} limit={3} label="Depositum" />
        </FieldRow>
        <FieldRow label="Betalingsdato for depositum">
          <input style={inputStyle} type="date" value={state.deposit_payment_date}
            onChange={e => onChange({ deposit_payment_date: e.target.value })} />
        </FieldRow>
      </SectionBlock>

      {/* ── Forudbetalt leje ─────────────────────────────────────────────── */}
      <SectionBlock icon={<IconCalendar />} title="Forudbetalt leje" subtitle="Forudbetalt leje ved indflytning">
        <FieldRow label="Forudbetalt leje" required>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              type="text"
              value={state.prepaid_rent_dkk}
              onChange={e => onChange({ prepaid_rent_dkk: e.target.value })}
              placeholder="8.500"
            />
            <span style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }}>kr.</span>
          </div>
          <MonthPills monthly={state.monthly_rent_dkk} value={state.prepaid_rent_dkk} onChange={v => onChange({ prepaid_rent_dkk: v })} />
          <OverLimitWarn monthly={state.monthly_rent_dkk} value={state.prepaid_rent_dkk} limit={3} label="Forudbetalt leje" />
        </FieldRow>
        <FieldRow label="Betalingsdato for forudbetalt leje">
          <input style={inputStyle} type="date" value={state.prepaid_payment_date}
            onChange={e => onChange({ prepaid_payment_date: e.target.value })} />
        </FieldRow>
      </SectionBlock>

      {/* ── Engangsudgifter ──────────────────────────────────────────────── */}
      <SectionBlock icon={<IconReceipt />} title="Engangsudgifter" subtitle="Engangsbetalinger ved indflytning (max 3)">
        {state.engangsudgifter.length === 0 && (
          <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 12px' }}>Ingen engangsudgifter tilføjet endnu.</p>
        )}
        {state.engangsudgifter.map((e, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
            <input style={{ ...inputStyle, flex: 2 }} type="text" placeholder="Beskrivelse (f.eks. Nøgledeponering)"
              value={e.name} onChange={ev => updateEngangsnavn(i, ev.target.value)} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
              <input style={{ ...inputStyle, flex: 1 }} type="text" placeholder="0"
                value={e.dkk} onChange={ev => updateEngangsbeloeb(i, ev.target.value)} />
              <span style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }}>kr.</span>
            </div>
            <button type="button" onClick={() => removeEngangslinje(i)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '6px 4px', fontSize: 18, lineHeight: 1 }}>×</button>
          </div>
        ))}
        {state.engangsudgifter.length < 3 && (
          <button type="button" onClick={addEngangslinje}
            style={{ marginTop: 4, padding: '6px 14px', borderRadius: 8, border: '1px dashed #d1d5db', background: '#fafafa', fontSize: 13, color: '#6b7280', cursor: 'pointer' }}>
            + Tilføj engangsudgift
          </button>
        )}
      </SectionBlock>

      {/* ── Øvrige indbetalinger ─────────────────────────────────────────── */}
      <SectionBlock icon={<IconCalendar />} title="Øvrige indbetalinger" subtitle="Betalingsdag og sammensætning af første måneds betaling">
        <FieldRow label="Betalingsdag for første månedlige husleje">
          <input style={inputStyle} type="date" value={state.first_payment_date}
            onChange={e => onChange({ first_payment_date: e.target.value })} />
        </FieldRow>
        <div style={{ marginTop: 14, borderRadius: 8, border: '1px solid #e8eaed', overflow: 'hidden' }}>
          <div style={{ background: '#f7f8fa', padding: '9px 14px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e8eaed' }}>
            Første måneds betaling
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {state.engangsudgifter.map((e, i) => (
              <SumRow key={i} label={e.name || `Engangsudgift ${i + 1}`} amount={parseDkk(e.dkk)} />
            ))}
            <SumRow label="Leje for første periode" amount={foersteLeje} />
            {acontoTotal > 0 && <SumRow label="A conto i alt" amount={acontoTotal} />}
            <div style={{ marginTop: 4, paddingTop: 8, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: '#111827' }}>
              <span>I alt (øvrige)</span>
              <span>{fmtDkk(ovrigt)} kr.</span>
            </div>
          </div>
          {naesteHuslejeIso && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #e8eaed', fontSize: 12, color: '#6b7280' }}>
              Første gang der herefter betales husleje er den{' '}
              <strong style={{ color: '#374151' }}>{fmtDate(naesteHuslejeIso).replace('d. ', '')}</strong>
            </div>
          )}
        </div>
      </SectionBlock>

      {/* ── Indbetalinger inden indflytning — grand summary ──────────────── */}
      <div style={{ borderRadius: 10, border: '1px solid #e8eaed', background: '#fff', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ width: 38, height: 38, borderRadius: 8, background: '#f7f8fa', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
            <IconSummaryHeader />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Indbetalinger inden indflytning</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              Oversigt over betalinger som lejer skal foretage inden indflytning
            </div>
          </div>
        </div>

        {/* Rows */}
        <div style={{ padding: '0 18px' }}>
          <SummaryCardRow
            icon={<IconHandCoin />}
            label="Depositum"
            subtitle={depositSubtitle()}
            amount={parseDkk(state.deposit_dkk)}
            date={fmtDate(state.deposit_payment_date)}
          />
          <SummaryCardRow
            icon={<IconCoins />}
            label="Forudbetalt husleje"
            subtitle={prepaidSubtitle()}
            amount={parseDkk(state.prepaid_rent_dkk)}
            date={fmtDate(state.prepaid_payment_date)}
          />
          <SummaryCardRow
            icon={<IconWallet />}
            label="Øvrige indbetalinger"
            subtitle="Leje for første periode + første måneds a conto + engangsudgifter"
            amount={ovrigt}
            date={fmtDate(state.first_payment_date)}
          />
        </div>

        {/* Betaler lejeren i alt = depositum + forudbetalt leje + leje for første periode (PDF §4) */}
        <div style={{ padding: '11px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f3f4f6' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Betaler lejeren i alt</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{fmtDkk2(betalerIAlt)} kr.</span>
        </div>

        {/* Total */}
        <div style={{ padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f3f4f6', background: '#f7f8fa' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Total til betaling</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{fmtDkk2(grandTotal)} kr.</span>
        </div>
      </div>

    </div>
  )
}

function SumRow({ label, amount }: { label: string; amount: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280' }}>
      <span>{label}</span>
      <span>{amount ? fmtDkk(amount) + ' kr.' : '—'}</span>
    </div>
  )
}
